//@ts-nocheck
import { consoleMsg } from "../shared/utility.js";

const app = window.app;

export class PhotoManager {
	constructor(name) {
		this.name = name;
	}

	static base64ToBlob(base64, mimeType = "image/jpeg") {
		const byteCharacters = atob(base64);
		const byteNumbers = new Array(byteCharacters.length);
		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}
		const byteArray = new Uint8Array(byteNumbers);
		return new Blob([byteArray], { type: mimeType });
	}

	static blobToBase64(blob) {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => {
				resolve(reader.result.split(",")[1]);
			};
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
	}

	/* createImageBitmap applies the image's EXIF orientation tag; drawImage from a plain
	<img> does not do so reliably across browsers, which is why portrait phone photos come
	out sideways or stretched. Falls back to an <img> decode if it's unavailable. */
	static async decodeImage(blob) {
		if (typeof createImageBitmap === "function") {
			try {
				return await createImageBitmap(blob, { imageOrientation: "from-image" });
			} catch (error) {
				consoleMsg("createImageBitmap failed, falling back to Image decode: ", error);
			}
		}

		return await new Promise((resolve, reject) => {
			const img = new Image();
			const url = URL.createObjectURL(blob);
			img.onload = () => {
				URL.revokeObjectURL(url);
				resolve(img);
			};
			img.onerror = (error) => {
				URL.revokeObjectURL(url);
				reject(error);
			};
			img.src = url;
		});
	}

	static async measureRatio(blob) {
		const image = await PhotoManager.decodeImage(blob);
		const ratio = (image.naturalHeight || image.height) / (image.naturalWidth || image.width);
		if (typeof image.close === "function") image.close();
		return ratio;
	}

	/* A portrait frame centre-cropped to a landscape ratio loses ~44% of its height —
	roof and wheels. Rotating 90° clockwise first keeps the whole photo; the subject
	lies on its side, which is what the driver sees in the tile too. */
	static rotateToLandscape(source) {
		const srcW = source.naturalWidth || source.width;
		const srcH = source.naturalHeight || source.height;

		const canvas = document.createElement("canvas");
		canvas.width = srcH;
		canvas.height = srcW;

		const ctx = canvas.getContext("2d");
		ctx.translate(srcH, 0);
		ctx.rotate(Math.PI / 2);
		ctx.drawImage(source, 0, 0);

		return canvas;
	}

	static async convertBlobToJpeg(blob, quality = 1.0, maxWidth = Infinity, aspectRatio) {
		let source = await PhotoManager.decodeImage(blob);

		// Only inspection angles request a landscape ratio; damage photos stay as shot
		if (aspectRatio > 1 && (source.naturalHeight || source.height) > (source.naturalWidth || source.width)) {
			const rotated = PhotoManager.rotateToLandscape(source);
			if (typeof source.close === "function") source.close();
			source = rotated;
		}

		const srcW = source.naturalWidth || source.width;
		const srcH = source.naturalHeight || source.height;

		// Centre-crop to aspectRatio (width / height), matching what object-fit: cover shows
		let sx = 0;
		let sy = 0;
		let sWidth = srcW;
		let sHeight = srcH;

		if (aspectRatio) {
			if (srcW / srcH > aspectRatio) {
				sWidth = Math.round(srcH * aspectRatio);
				sx = Math.round((srcW - sWidth) / 2);
			} else {
				sHeight = Math.round(srcW / aspectRatio);
				sy = Math.round((srcH - sHeight) / 2);
			}
		}

		let width = sWidth;
		let height = sHeight;
		if (width > maxWidth) {
			height = Math.round((height * maxWidth) / width);
			width = maxWidth;
		}

		const canvas = document.createElement("canvas");
		canvas.width = width;
		canvas.height = height;
		canvas.getContext("2d").drawImage(source, sx, sy, sWidth, sHeight, 0, 0, width, height);
		if (typeof source.close === "function") source.close();

		return await new Promise((resolve) => canvas.toBlob((jpegBlob) => resolve(jpegBlob), "image/jpeg", quality));
	}

	static async storePicDb(img, imgRow, questionId, explicitKey) {
		const processing = document.createElement("ion-loading");
		processing.message = "Processing image to reduce storage size...";
		document.body.appendChild(processing);
		await processing.present();

		try {
			let photo = {};
			let photoId = explicitKey || img.getAttribute("name") || img.id;
			let label = typeof this.assignPhotoLabel === "function" ? this.assignPhotoLabel(photoId) : undefined;

			const response = await fetch(img.src);
			const isCarPhoto = Boolean(photoId && photoId.startsWith("car-"));
			const maxWidth = isCarPhoto ? 2560 : 1600;

			// Inspection angles display 4:3 via .landscape-img (object-fit: cover) — crop the
			// pixels to match so the PDF renders what the driver actually saw
			const blob = await this.convertBlobToJpeg(await response.blob(), 0.8, maxWidth, isCarPhoto ? 4 / 3 : undefined);
			const base64Data = await this.blobToBase64(blob);
			// Measured from the stored image, so it reflects the crop and the EXIF correction
			const imgRatio = await PhotoManager.measureRatio(blob);
			const storageKey = app.state.storageKey;

			if (!isCarPhoto && (app.state.currentPage === "page2" || app.state.currentPage === "page11")) {
				photoId = img.id;
				photo = {
					id: photoId,
					ratio: imgRatio,
					row: imgRow.id,
					questionId: questionId,
					photo: base64Data,
				};
			} else {
				photo = { id: photoId, ratio: imgRatio, photo: base64Data };
				if (label) photo.label = label;
			}
			await store.set(`${storageKey}_${photoId}`, photo);
			consoleMsg("STORING PIC WITH NEW PHOTO MANAGER BASE CLASS...");
			consoleMsg("Image stored in IndexedDB as Base64 (JPEG format)");
		} catch (error) {
			console.error("Error storing image:", error);
		} finally {
			await processing.dismiss();
			processing.remove();
		}
	}

	static assignPhotoLabel(photoId) {
		switch (photoId) {
			case "car-front":
				return "Front";
			case "car-driver-side":
				return "Driver Side";
			case "car-passenger-side":
				return "Passenger Side";
			case "car-back":
				return "Rear";
			case "car-interior-front":
				return "Interior Front";
			case "car-interior-rear":
				return "Interior Rear";
			case "car-vin":
				return "VIN Label";
			case "car-top":
				return "Top";
			case "car-driver-profile":
				return "Driver Profile";
			case "car-passenger-profile":
				return "Passenger Profile";
			case "car-driver-rear-profile":
				return "Car Rear Driver Profile";
			case "car-passenger-rear-profile":
				return "Car Rear Passenger Profile";
			case "car-trunk":
				return "Trunk";
			case "car-odometer":
				return "Odometer";
			default:
				return "";
		}
	}

	async takePhoto(...args) {
		const file = await PhotoManager.captureViaFileInput();
		const webPath = URL.createObjectURL(file);
		await this.savePhoto(...args, webPath);
	}

	/* True when the device is likely to hand `capture` off to a real camera app.
	Desktop browsers accept the capture attribute and silently ignore it, so a
	"Take Photo" option there would just reopen the same file picker. */
	static supportsCameraCapture() {
		return window.matchMedia?.("(pointer: coarse)").matches === true && navigator.maxTouchPoints > 0;
	}

	static captureViaFileInput(inputId = "camera-input") {
		return new Promise((resolve, reject) => {
			const input = document.getElementById(inputId);
			input.value = "";
			input.onchange = () => {
				if (input.files?.[0]) resolve(input.files[0]);
				else reject(new Error("No file selected"));
			};
			input.click();
		});
	}
}
